import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * PB's sentinel for "no workstation" —
 * `uo_phfc_global_constant_cntr.sru:45: constant string xs_workstation_id_default =
 * '(SYSTEM-DEFAULT)'`. It is a real `dbxx.workstation` row that PB's unfiltered dddw shows in
 * the list, and `w_login.srw:281-288` (itemchanged) nulls the column when the operator picks it.
 * `select` below reproduces that: picking the sentinel clears the selection.
 *
 * The BE also flags the row (`WorkstationOption.systemDefault`), so callers should prefer that
 * flag over string-comparing; this constant exists for the store's own clear-on-select rule and
 * for tests.
 */
export const SYSTEM_DEFAULT_WORKSTATION = '(SYSTEM-DEFAULT)';

export interface WorkstationSelection {
  workstationId: string;
  description: string;
}

interface WorkstationState {
  /** null = no station chosen; consumers fall back to PB's `(SYSTEM-DEFAULT)` layer. */
  current: WorkstationSelection | null;
}

interface WorkstationActions {
  /**
   * Pick a station. Two inputs clear the selection rather than storing anything:
   * the `(SYSTEM-DEFAULT)` sentinel (PB `w_login.srw:281-288`) and a blank id (PB
   * `w_login.srw:194` / `f_login.srf:280` collapse empty to the sentinel, and the picker's
   * `—` option is how an operator un-sets a station). Storing a blank id would leave a junk
   * `{workstationId:''}` object in localStorage that reads as "set" to any future consumer.
   */
  select: (s: WorkstationSelection) => void;
  clear: () => void;
}

/**
 * Workstation identity for THIS BROWSER PROFILE ON THIS MACHINE — ADR-0039 part A.
 *
 * ## Why this is not a user preference
 *
 * `shared/prefs/storage.ts` namespaces every key as `ph:<userId>:<key>` and its header comment
 * promises a future per-user `usr_prefs` table. **Do not move this there.** PB identifies the
 * workstation by the *machine*, not the operator:
 *
 * - `w_login.srw:68-73` falls back to
 *   `select max(workstation_id) from dbxx.workstation where host_name = <computer name>`
 * - the INI key it reads first is `Client`/`WorkStation` — a per-install file (`w_login.srw:63`)
 * - `f_login.srf:166-185` accepts `-W<id>` on the command line of that install
 *
 * Two operators sharing one pack station must resolve the *same* per-station settings
 * (`crt_configuration` pack location, carton type, autoprint flags; `workstation_setting`
 * scale/printer). A per-user value would silently follow the person between stations — the
 * "the pack location keeps changing on me" bug class. Hence: NOT namespaced by userId, and the
 * key deliberately sits alongside `ph10-auth` rather than under the `ph:` pref prefix.
 *
 * Nothing reads the selection yet beyond `shared/api/client.ts`, which forwards it as
 * `X-PH10-Workstation`. Wiring `crt_configuration` / `workstation_setting` to it is the
 * follow-up slice, gated on Chris accepting ADR-0039.
 */
export const useWorkstationStore = create<WorkstationState & WorkstationActions>()(
  persist(
    (set) => ({
      current: null,
      select: (s) => {
        const id = s.workstationId.trim();
        const cleared = id === '' || id === SYSTEM_DEFAULT_WORKSTATION;
        set({
          current: cleared ? null : { workstationId: id, description: s.description.trim() },
        });
      },
      clear: () => set({ current: null }),
    }),
    {
      name: 'ph10-workstation',
      partialize: (s) => ({ current: s.current }),
    },
  ),
);

/**
 * The workstation id to send on the wire, or null when none is chosen. Read imperatively (not
 * as a hook) so non-React callers — `shared/api/client.ts` — can use it.
 */
export function currentWorkstationId(): string | null {
  return useWorkstationStore.getState().current?.workstationId ?? null;
}
